import type { FormEventHandler, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CargoProblemAssigneeInputSchema,
  type CargoProblemAssigneeInput,
  type CargoProblemDetail,
} from '@magimp/shared';
import { Banner, Button, EditorModal, FormRow, Select, useEditorGuard } from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import { useToastStore } from '../../../stores/toast.js';
import { useProblemAssigneeCandidates, useReassignCargoProblem } from '../queries.js';

const REASSIGN_FORM_ID = 'cargo-problem-reassign-form';

export function CargoProblemReassignModal({
  problem,
  onClose,
}: {
  problem: CargoProblemDetail;
  onClose: () => void;
}) {
  const { t } = useTranslation('cargoProblems');
  const showToast = useToastStore((state) => state.show);
  const candidates = useProblemAssigneeCandidates(problem.id);
  const reassign = useReassignCargoProblem(problem.id);
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<CargoProblemAssigneeInput>({
    resolver: zodResolver(CargoProblemAssigneeInputSchema),
    mode: 'onTouched',
    defaultValues: { assigneeId: '' },
  });
  const assigneeId = watch('assigneeId');
  const dirty = isDirty && assigneeId !== problem.assignee?.id;
  const currentUnavailable = Boolean(
    problem.assignee &&
    !candidates.isLoading &&
    !candidates.isError &&
    !candidates.data?.candidates.some((candidate) => candidate.id === problem.assignee?.id),
  );

  async function confirm(values: CargoProblemAssigneeInput) {
    if (values.assigneeId === problem.assignee?.id) return;
    try {
      await reassign.mutateAsync(values.assigneeId);
      showToast({ tone: 'success', text: t('toasts.reassigned') });
      onClose();
    } catch (error) {
      showToast({ tone: 'error', text: errorMessage(error) });
    }
  }

  return (
    <EditorModal
      open
      title={t('reassign.title')}
      dirty={dirty}
      busy={reassign.isPending}
      onConfirmClose={onClose}
      footer={
        <ReassignFooter
          busy={reassign.isPending}
          disabled={
            candidates.isLoading ||
            candidates.isError ||
            Boolean(assigneeId && assigneeId === problem.assignee?.id)
          }
          formId={REASSIGN_FORM_ID}
        />
      }
    >
      <GuardedReassignForm id={REASSIGN_FORM_ID} onSubmit={handleSubmit(confirm)} onClose={onClose}>
        <fieldset className="cargo-problem-transition-form" disabled={reassign.isPending}>
          <FormRow label={t('reassign.current')}>
            <div className="cargo-problem-selected-cargo">
              <span>{problem.assignee?.name ?? t('unassigned')}</span>
              {currentUnavailable ? (
                <span className="cargo-problem-unavailable-label">
                  {t('editor.assigneeUnavailable')}
                </span>
              ) : null}
            </div>
          </FormRow>
          {currentUnavailable ? (
            <Banner tone="warn">{t('reassign.currentUnavailableHelp')}</Banner>
          ) : null}
          <FormRow
            label={t('reassign.next')}
            error={errors.assigneeId ? t('validation.assigneeRequired') : undefined}
          >
            <Controller
              control={control}
              name="assigneeId"
              render={({ field }) => (
                <Select
                  value={field.value}
                  disabled={candidates.isLoading}
                  placeholder={t('editor.assigneePlaceholder')}
                  onChange={field.onChange}
                  options={(candidates.data?.candidates ?? []).map((candidate) => ({
                    value: candidate.id,
                    label: candidate.name,
                  }))}
                />
              )}
            />
          </FormRow>
          {candidates.isError ? <Banner tone="error">{t('reassign.loadError')}</Banner> : null}
          <p className="cargo-problem-cap-note">{t('reassign.slaHelp')}</p>
        </fieldset>
      </GuardedReassignForm>
    </EditorModal>
  );
}

function GuardedReassignForm({
  id,
  onSubmit,
  onClose,
  children,
}: {
  id: string;
  onSubmit: FormEventHandler<HTMLFormElement>;
  onClose: () => void;
  children: ReactNode;
}) {
  const { confirmDestructive } = useEditorGuard();
  return (
    <form
      id={id}
      onSubmit={onSubmit}
      onReset={(event) => {
        event.preventDefault();
        confirmDestructive(onClose);
      }}
      noValidate
    >
      {children}
    </form>
  );
}

function ReassignFooter({
  busy,
  disabled,
  formId,
}: {
  busy: boolean;
  disabled: boolean;
  formId: string;
}) {
  const { t } = useTranslation('cargoProblems');
  return (
    <>
      <Button type="reset" form={formId} kind="ghost" disabled={busy}>
        {t('actions.cancel')}
      </Button>
      <Button type="submit" form={formId} kind="primary" disabled={disabled || busy}>
        {t('actions.reassign')}
      </Button>
    </>
  );
}
